// Animation library — pure DOM + CSS, no third-party deps. Designed so
// each helper is fire-and-forget and self-cleans its DOM nodes after the
// animation finishes, so callers don't need to track anything.
export function captureCardRects(root) {
    const rects = new Map();
    root.querySelectorAll('.pcard[data-card-id]').forEach((el) => {
        rects.set(el.dataset.cardId, el.getBoundingClientRect());
    });
    return rects;
}
export function playFlip(root, before, durationMs = 600) {
    root.querySelectorAll('.pcard[data-card-id]').forEach((el) => {
        const id = el.dataset.cardId;
        const prev = before.get(id);
        if (!prev) {
            // Card is freshly arrived → slight thunk-in animation
            el.animate([
                { transform: 'translateY(-30px) scale(0.92) rotate(-2deg)', opacity: 0 },
                { transform: 'translateY(8px) scale(1.04) rotate(0deg)', opacity: 1, offset: 0.65 },
                { transform: 'translateY(0) scale(1) rotate(0deg)', opacity: 1 },
            ], { duration: 380, easing: 'cubic-bezier(0.34, 1.4, 0.64, 1)', fill: 'none' });
            return;
        }
        const now = el.getBoundingClientRect();
        const dx = prev.left - now.left;
        const dy = prev.top - now.top;
        if (dx === 0 && dy === 0)
            return;
        // Slight arc midway
        const midX = dx * 0.5;
        const midY = (dy * 0.5) - Math.abs(dx) * 0.08; // arc up a little
        el.animate([
            { transform: `translate(${dx}px, ${dy}px) rotate(0deg)` },
            { transform: `translate(${midX}px, ${midY}px) rotate(2deg)`, offset: 0.5 },
            { transform: 'translate(0, 0) rotate(0deg)' },
        ], { duration: durationMs, easing: 'cubic-bezier(0.2, 0.8, 0.25, 1)', fill: 'none' });
    });
}
// ─── Counter tween (used by hull number) ───────────────────────────
export function tweenNumber(from, to, duration, onFrame) {
    const start = performance.now();
    let raf = 0;
    const tick = (t) => {
        const p = Math.min(1, (t - start) / duration);
        const eased = 1 - Math.pow(1 - p, 3);
        const v = Math.round(from + (to - from) * eased);
        onFrame(v);
        if (p < 1)
            raf = requestAnimationFrame(tick);
        else
            onFrame(to);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
}
// ─── Pattern banner (sequenced) ────────────────────────────────────
export function flashPatternBanner(text, patternType) {
    const el = document.createElement('div');
    el.className = `pattern-banner${patternType ? ` pattern-banner--${patternType}` : ''}`;
    el.innerHTML = `<span class="pattern-banner-icon">${patternIcon(patternType)}</span>${escapeHtml(text)}`;
    document.body.appendChild(el);
    void el.offsetWidth;
    el.classList.add('pattern-banner--in');
    const linger = Math.max(2200, Math.min(5000, text.length * 90));
    setTimeout(() => {
        el.classList.add('pattern-banner--out');
        setTimeout(() => el.remove(), 600);
    }, linger);
}
// Single condensed banner showing every pattern from the turn in one card,
// with an optional swing line (earn / steal totals). Replaces the long
// stagger cascade — one popup, one pulse, then gone.
export function flashComboBanner(patterns, totals) {
    if (patterns.length === 0)
        return;
    const dominant = patterns.find((p) => p.type === 'mutiny')?.type
        ?? patterns.find((p) => p.type === 'straight-flush')?.type
        ?? patterns[0].type;
    const chips = patterns.map((p) => `<span class="combo-chip"><span class="combo-chip-icon">${patternIcon(p.type)}</span>${escapeHtml(p.label)}</span>`).join('');
    const swingParts = [];
    if (totals?.earn)
        swingParts.push(`<span class="combo-swing-earn">${totals.earn > 0 ? '+' : ''}${totals.earn}🪙</span>`);
    if (totals?.steal && totals.steal > 0)
        swingParts.push(`<span class="combo-swing-steal">⚔ ${totals.steal}</span>`);
    if (totals?.forceDiscard && totals.forceDiscard > 0)
        swingParts.push(`<span class="combo-swing-fd">🌊 ${totals.forceDiscard}</span>`);
    const swing = swingParts.length > 0 ? `<div class="combo-swing">${swingParts.join(' · ')}</div>` : '';
    const headline = patterns.length > 1 ? `Combo ×${patterns.length}` : patterns[0].label;
    const el = document.createElement('div');
    el.className = `combo-banner${dominant ? ` combo-banner--${dominant}` : ''}`;
    el.innerHTML = `
    <div class="combo-banner-headline">${escapeHtml(headline)}</div>
    <div class="combo-banner-chips">${chips}</div>
    ${swing}
  `;
    document.body.appendChild(el);
    void el.offsetWidth;
    el.classList.add('combo-banner--in');
    setTimeout(() => {
        el.classList.add('combo-banner--out');
        setTimeout(() => el.remove(), 500);
    }, 1700);
}
// Stagger a sequence of banners — each one displayed briefly with overlap.
// Now accepts {label, type} pairs for per-pattern styling.
export function sequenceBanners(items) {
    if (items.length === 0)
        return;
    const norm = items.map((it) => typeof it === 'string' ? { label: it, type: undefined } : it);
    if (norm.length === 1) {
        flashPatternBanner(norm[0].label, norm[0].type);
        return;
    }
    const PER_LINGER = 1100;
    const STAGGER = 480;
    norm.forEach((it, i) => {
        setTimeout(() => {
            const el = document.createElement('div');
            el.className = `pattern-banner pattern-banner--stack${it.type ? ` pattern-banner--${it.type}` : ''}`;
            el.style.setProperty('--banner-stack-i', String(i));
            el.innerHTML = `<span class="pattern-banner-icon">${patternIcon(it.type)}</span>${escapeHtml(it.label)}`;
            document.body.appendChild(el);
            void el.offsetWidth;
            el.classList.add('pattern-banner--in');
            setTimeout(() => {
                el.classList.add('pattern-banner--out');
                setTimeout(() => el.remove(), 500);
            }, PER_LINGER);
        }, i * STAGGER);
    });
}
function patternIcon(type) {
    switch (type) {
        case 'double': return '🃏';
        case 'triple': return '🎯';
        case 'sandwich': return '🥪';
        case 'marriage': return '💍';
        case 'run': return '🏃';
        case 'suit-chain': return '⛓';
        case 'flush': return '🌊';
        case 'straight-flush': return '⚡';
        case 'royalty': return '👑';
        case 'mutiny': return '💥';
        case 'parity-squad': return '⚖';
        default: return '⚓';
    }
}
function escapeHtml(s) {
    return s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}
// ─── Sparks / particles ────────────────────────────────────────────
export function sparkleOnElement(el, color = '#f0c75a') {
    const rect = el.getBoundingClientRect();
    for (let i = 0; i < 8; i++) {
        const sp = document.createElement('div');
        sp.className = 'coin-spark';
        sp.style.left = `${rect.left + rect.width / 2}px`;
        sp.style.top = `${rect.top + rect.height / 2}px`;
        sp.style.background = color;
        sp.style.setProperty('--dx', `${(Math.random() - 0.5) * 110}px`);
        sp.style.setProperty('--dy', `${-30 - Math.random() * 70}px`);
        document.body.appendChild(sp);
        setTimeout(() => sp.remove(), 900);
    }
}
// Coin trail: animate flying coins from one element to another.
// Used for steals (opp's hull → your hull) and shop buys (your hull → offer).
export function coinTrail(from, to, count = 6, color = '#f0c75a') {
    const fromRect = from.getBoundingClientRect();
    const toRect = to.getBoundingClientRect();
    const startX = fromRect.left + fromRect.width / 2;
    const startY = fromRect.top + fromRect.height / 2;
    const endX = toRect.left + toRect.width / 2;
    const endY = toRect.top + toRect.height / 2;
    for (let i = 0; i < count; i++) {
        const coin = document.createElement('div');
        coin.className = 'flying-coin';
        coin.style.left = `${startX}px`;
        coin.style.top = `${startY}px`;
        coin.style.background = color;
        coin.style.color = color;
        coin.textContent = '🪙';
        const delay = i * 60;
        document.body.appendChild(coin);
        coin.animate([
            { transform: `translate(0, 0) scale(0.6) rotate(0deg)`, opacity: 0 },
            { transform: `translate(0, -10px) scale(1) rotate(120deg)`, opacity: 1, offset: 0.15 },
            { transform: `translate(${(endX - startX) * 0.6}px, ${(endY - startY) * 0.5 - 30}px) scale(1) rotate(360deg)`, opacity: 1, offset: 0.6 },
            { transform: `translate(${endX - startX}px, ${endY - startY}px) scale(0.4) rotate(720deg)`, opacity: 0 },
        ], { duration: 900, delay, easing: 'cubic-bezier(0.25, 0.7, 0.4, 1)', fill: 'forwards' });
        setTimeout(() => coin.remove(), 900 + delay);
    }
}
// Floating delta: "+5 earn" floats up and fades from an anchor element.
export function floatingDelta(anchor, text, color = '#f0c75a') {
    const rect = anchor.getBoundingClientRect();
    const el = document.createElement('div');
    el.className = 'floating-delta';
    el.textContent = text;
    el.style.left = `${rect.left + rect.width / 2}px`;
    el.style.top = `${rect.top}px`;
    el.style.color = color;
    document.body.appendChild(el);
    el.animate([
        { transform: 'translate(-50%, 0) scale(0.85)', opacity: 0 },
        { transform: 'translate(-50%, -10px) scale(1)', opacity: 1, offset: 0.2 },
        { transform: 'translate(-50%, -60px) scale(1)', opacity: 1, offset: 0.7 },
        { transform: 'translate(-50%, -90px) scale(0.85)', opacity: 0 },
    ], { duration: 1400, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)', fill: 'forwards' });
    setTimeout(() => el.remove(), 1400);
}
// ─── Ante / round fanfares ──────────────────────────────────────────
export function antesFanfare(text) {
    const el = document.createElement('div');
    el.className = 'ante-burst';
    el.innerHTML = `
    <div class="ante-burst-glow"></div>
    <div class="ante-burst-text">${text}</div>
  `;
    document.body.appendChild(el);
    setTimeout(() => el.classList.add('ante-burst--out'), 1800);
    setTimeout(() => el.remove(), 2400);
}
// ─── End-of-match ceremonies ────────────────────────────────────────
export function showWinCeremony(captainName, reason) {
    const el = document.createElement('div');
    el.className = 'end-ceremony end-ceremony--win';
    el.innerHTML = `
    <div class="ceremony-bg"></div>
    <div class="ceremony-rays"></div>
    <div class="ceremony-chest">
      <div class="ceremony-chest-lid"></div>
      <div class="ceremony-chest-body"></div>
      <div class="ceremony-chest-glow"></div>
    </div>
    <div class="ceremony-coins"></div>
    <h1 class="ceremony-title">VICTORY</h1>
    <h2 class="ceremony-sub">${captainName}</h2>
    <p class="ceremony-reason">${reason}</p>
  `;
    document.body.appendChild(el);
    // Coin shower
    const coinHost = el.querySelector('.ceremony-coins');
    for (let i = 0; i < 30; i++) {
        const c = document.createElement('div');
        c.className = 'rain-coin';
        c.textContent = '🪙';
        c.style.left = `${Math.random() * 100}%`;
        c.style.animationDelay = `${Math.random() * 2}s`;
        c.style.animationDuration = `${2.5 + Math.random() * 2}s`;
        c.style.fontSize = `${18 + Math.random() * 18}px`;
        coinHost.appendChild(c);
    }
    return el;
}
export function showLoseCeremony(message, reason) {
    const el = document.createElement('div');
    el.className = 'end-ceremony end-ceremony--lose';
    el.innerHTML = `
    <div class="ceremony-bg"></div>
    <div class="sea-wave"></div>
    <div class="sinking-ship">
      <div class="ship-hull"></div>
      <div class="ship-mast"></div>
      <div class="ship-sail"></div>
      <div class="ship-flag">☠</div>
    </div>
    <div class="bubbles">
      ${Array.from({ length: 12 }).map((_, i) => `<div class="bubble" style="left:${10 + Math.random() * 80}%;animation-delay:${i * 0.3}s"></div>`).join('')}
    </div>
    <h1 class="ceremony-title ceremony-title--dark">${message}</h1>
    <p class="ceremony-reason">${reason}</p>
  `;
    document.body.appendChild(el);
    return el;
}
// ─── Hull pulse helpers ─────────────────────────────────────────────
export function setHullPulse(el, kind) {
    el.classList.remove('hull--pulse-danger', 'hull--pulse-glory');
    if (kind === 'danger')
        el.classList.add('hull--pulse-danger');
    else if (kind === 'glory')
        el.classList.add('hull--pulse-glory');
}
// ─── Page transition ───────────────────────────────────────────────
export function pageFadeIn(root) {
    root.classList.add('route-fade-in');
    setTimeout(() => root.classList.remove('route-fade-in'), 360);
}
/** Brief screen shake on the app container. */
export function screenShake(intensity = 'medium') {
    const target = document.getElementById('app') ?? document.body;
    const cls = `screen-shake screen-shake--${intensity}`;
    target.classList.remove('screen-shake', 'screen-shake--soft', 'screen-shake--medium', 'screen-shake--hard');
    // Force reflow so re-adding the class restarts the animation
    void target.offsetWidth;
    target.classList.add(...cls.split(' '));
    const dur = intensity === 'hard' ? 520 : intensity === 'medium' ? 380 : 240;
    setTimeout(() => target.classList.remove('screen-shake', `screen-shake--${intensity}`), dur);
}
/** Full-screen flash overlay. Use 'red' for damage, 'gold' for big earn/steal. */
export function flashOverlay(color = 'red', durationMs = 220) {
    const el = document.createElement('div');
    el.className = `flash-overlay flash-overlay--${color}`;
    document.body.appendChild(el);
    el.animate([{ opacity: 0 }, { opacity: 0.55 }, { opacity: 0 }], { duration: durationMs, easing: 'ease-out' }).onfinish = () => el.remove();
}
/** Card-flip reveal: a freshly-drawn card flips face-up over 300ms. */
export function cardFlipReveal(cardEl, durationMs = 320) {
    cardEl.classList.add('card-flip-reveal');
    setTimeout(() => cardEl.classList.remove('card-flip-reveal'), durationMs + 30);
}
/** Squash-stretch when a card lands on the plank. */
export function cardLand(cardEl) {
    cardEl.animate([
        { transform: 'scale(1.0)' },
        { transform: 'scale(1.10) translateY(-3px)' },
        { transform: 'scale(0.96)' },
        { transform: 'scale(1.0)' },
    ], { duration: 220, easing: 'ease-out' });
}
/** Whoosh + arc when a card flies to a pile (sold/tossed/overboard). */
export function discardWhoosh(cardEl, target, durationMs = 360) {
    const a = cardEl.getBoundingClientRect();
    const b = target.getBoundingClientRect();
    const dx = (b.left + b.width / 2) - (a.left + a.width / 2);
    const dy = (b.top + b.height / 2) - (a.top + a.height / 2);
    cardEl.animate([
        { transform: 'translate(0, 0) rotate(0)', opacity: 1 },
        { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 30}px) rotate(180deg)`, opacity: 0.85, offset: 0.5 },
        { transform: `translate(${dx}px, ${dy}px) rotate(360deg)`, opacity: 0 },
    ], { duration: durationMs, easing: 'cubic-bezier(0.4, 0, 1, 1)' });
}
/** Big floating damage/earn number that scales with magnitude. */
export function damageNumber(amount, anchor, kind = 'damage') {
    const r = anchor.getBoundingClientRect();
    const el = document.createElement('div');
    el.className = `damage-number damage-number--${kind}`;
    const abs = Math.abs(amount);
    if (abs >= 8)
        el.classList.add('damage-number--huge');
    else if (abs >= 4)
        el.classList.add('damage-number--big');
    el.textContent = `${kind === 'damage' ? '-' : '+'}${abs}`;
    el.style.left = `${r.left + r.width / 2}px`;
    el.style.top = `${r.top + r.height / 2}px`;
    document.body.appendChild(el);
    el.animate([
        { transform: 'translate(-50%, -50%) scale(0.6)', opacity: 0 },
        { transform: 'translate(-50%, -120%) scale(1.0)', opacity: 1, offset: 0.4 },
        { transform: 'translate(-50%, -200%) scale(0.95)', opacity: 0 },
    ], { duration: 1100, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }).onfinish = () => el.remove();
}
/** Toggle a low-hull vignette pulse on the app container. */
export function lowHullVignette(active) {
    const target = document.getElementById('app') ?? document.body;
    target.classList.toggle('low-hull-vignette', active);
}
// ─── Cannon combat: fire from one element to another ───────────────
/** Launch N cannonballs from `fromEl` to `toEl` with muzzle flash on
 * launch and smoke + impact flash on landing. Audio is left to the
 * caller so this stays decoupled from the SFX registry. */
export function fireCannons(fromEl, toEl, count = 1, onImpact) {
    const a = fromEl.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    const fromX = a.left + a.width / 2;
    const fromY = a.top + a.height * 0.35;
    const toX = b.left + b.width / 2;
    const toY = b.top + b.height * 0.5;
    // Muzzle flash on launch
    for (let i = 0; i < count; i++) {
        const stagger = i * 140;
        setTimeout(() => muzzleFlash(fromX, fromY), stagger);
        setTimeout(() => launchBall(fromX, fromY, toX, toY, () => {
            smokePuff(toX, toY);
            onImpact?.(i);
        }), stagger + 50);
    }
}
function muzzleFlash(x, y) {
    const el = document.createElement('div');
    el.className = 'cannon-muzzle';
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    document.body.appendChild(el);
    el.animate([
        { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 1 },
        { transform: 'translate(-50%, -50%) scale(1.4)', opacity: 0.6, offset: 0.4 },
        { transform: 'translate(-50%, -50%) scale(2.0)', opacity: 0 },
    ], { duration: 280, easing: 'ease-out' }).onfinish = () => el.remove();
}
function launchBall(fromX, fromY, toX, toY, onArrive) {
    const el = document.createElement('div');
    el.className = 'cannon-ball';
    el.style.left = `${fromX}px`;
    el.style.top = `${fromY}px`;
    document.body.appendChild(el);
    const dx = toX - fromX;
    const dy = toY - fromY;
    const dist = Math.hypot(dx, dy);
    const dur = Math.max(380, Math.min(720, dist * 0.7));
    // Arc by sending it up midway, then down
    const arcHeight = Math.min(180, dist * 0.35);
    el.animate([
        { transform: 'translate(-50%, -50%) translate(0, 0)', opacity: 1 },
        { transform: `translate(-50%, -50%) translate(${dx * 0.5}px, ${dy * 0.5 - arcHeight}px)`, opacity: 1, offset: 0.5 },
        { transform: `translate(-50%, -50%) translate(${dx}px, ${dy}px)`, opacity: 0.9 },
    ], { duration: dur, easing: 'cubic-bezier(0.36, 0, 0.66, 1)' }).onfinish = () => {
        el.remove();
        onArrive();
    };
}
function smokePuff(x, y) {
    for (let i = 0; i < 4; i++) {
        const el = document.createElement('div');
        el.className = 'cannon-smoke';
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        document.body.appendChild(el);
        const driftX = (Math.random() - 0.5) * 80;
        const driftY = -30 - Math.random() * 60;
        el.animate([
            { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0.9 },
            { transform: `translate(-50%, -50%) translate(${driftX}px, ${driftY}px) scale(2.5)`, opacity: 0 },
        ], { duration: 750, easing: 'ease-out', delay: i * 40 }).onfinish = () => el.remove();
    }
    // White impact flash
    const flash = document.createElement('div');
    flash.className = 'cannon-impact';
    flash.style.left = `${x}px`;
    flash.style.top = `${y}px`;
    document.body.appendChild(flash);
    flash.animate([
        { transform: 'translate(-50%, -50%) scale(0.5)', opacity: 1 },
        { transform: 'translate(-50%, -50%) scale(2.4)', opacity: 0 },
    ], { duration: 280, easing: 'ease-out' }).onfinish = () => flash.remove();
}
